import { createApp, h } from 'vue'
import { createPinia } from 'pinia';
import App from './App.vue'
import router from './router'
import { useUserStore } from './store/userStore'
import vSelect from "vue-select";
import VueFeather from 'vue-feather';
import safeHtml from './directives/safeHtml';
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'

// [spike Fase 0] tokens del DS del GME nuevo — vía import CSS para que el css-loader
// de webpack resuelva los @import anidados (tokens/*.css). Ver docs/reskin-plan.md.
import '@gme/foundation/tokens.css'
// Footer del DS (superficie contour) — consumido por layouts/Footer.vue.
import '@gme/foundation/components/footer/footer.css'
// Tabs del DS — variante `.tabs.underline` (0.1.14) para los switchers inline
// de los cards (Monitor, Action Plan). Estado por Vue (sin tabs.js — ver a11y
// follow-up en docs/status.md).
import '@gme/foundation/elements/tabs/tabs.css'
// Card + price-block + feature-list — los tres elements que compone el `ProductCard`
// del website. Los consume el upsell a Cohort (`UpgradeContent`), que ahora arma sus
// tiles de plan con el MISMO CSS del DS que el sitio, en vez de con cards a mano.
import '@gme/foundation/elements/card/card.css'
import '@gme/foundation/elements/price-block/price-block.css'
import '@gme/foundation/elements/feature-list/feature-list.css'
// Section + media-bg + hero — el andamio editorial del DS, para las páginas
// públicas (perfil público, landing). Hasta ahora la app reimplementaba esto a
// mano: `_public-profile.scss` tenía 1927 líneas de cards encajonadas para lo que
// el sistema ya resuelve full-bleed, con cascada de superficie y scrims.
//
// El ORDEN importa: `hero` es una composición sobre `section` + `media-bg`
// (hero.css § "Hero NO es un componente standalone"), así que va último.
//
// `nav` va primero porque declara `--nav-h` en `:root`, y el pull-under-nav del
// hero (`margin-top: calc(-1 * (var(--nav-h) + 24px))`) lo consume. Sin ese token
// el calc queda inválido y el browser descarta la declaración entera — el hero
// perdía margin-top Y padding-top a la vez (ver docs/reskin-backlog.md, gotcha 45).
//
// NO importamos `nav.js`: es un IIFE que corre al load, hace
// `document.querySelectorAll('.nav')` una sola vez (en una SPA el nav de una vista
// ruteada todavía no existe) y setea `history.scrollRestoration = 'manual'` global.
// Los dos estados (`is-scrolled` + `body[data-nav-surface]`) los maneja Vue en
// `composables/useDsNav.js` — mismo criterio que tabs (CSS del DS, estado en Vue).
import '@gme/foundation/components/nav/nav.css'
import '@gme/foundation/components/section/section.css'
import '@gme/foundation/components/media-bg/media-bg.css'
import '@gme/foundation/components/hero/hero.css'
import './assets/scss/main.scss'
import i18n from './i18n'
import { captureUtmParams } from '@/composables/useDiagnostic'
import { useUpgradeOverlay } from '@/composables/useUpgradeOverlay';
import { initAnalytics, identifyUser } from '@/plugins/analytics';

// Persist UTM params from the landing URL across the SPA session.
// Used by Quick Diagnostic lead submission to attribute the source.
captureUtmParams();

const app = createApp(App);

// Global properties
app.config.globalProperties.$learnUrl = process.env.VUE_APP_LEARN;

// Upsell unificado: la capa `UpgradeOverlay` se monta una sola vez en App.vue y su
// estado vive en `useUpgradeOverlay`. Se exponen como globales porque los puntos de
// entrada están repartidos por toda la app (header, journey de launchpad, fichas de
// tour, reporte de plan) y así cada uno es un cambio de template, sin cablear el
// composable en el setup de cada componente.
//   $openUpgrade          → para <button>: abre la capa.
//   $onLockedUpgradeClick → para <router-link> con href real a /upgrade: intercepta el
//                           click normal y respeta ctrl/cmd-click y botón del medio.
const { openUpgrade: _openUpgrade, onLockedClick: _onLockedClick } = useUpgradeOverlay();
app.config.globalProperties.$openUpgrade = _openUpgrade;
app.config.globalProperties.$onLockedUpgradeClick = _onLockedClick;

app.use(i18n);
app.use(createPinia());

// Pinia state resets on every page reload. Restore selected child company +
// child companies list from localStorage so the UI renders correctly immediately.
const _userStore = useUserStore();
const _savedCompanyId = localStorage.getItem('gs-selected-company');
if (_savedCompanyId) {
  _userStore.setSelectedCompany(Number(_savedCompanyId));
}
const _savedChildren = localStorage.getItem('gs-child-companies');
if (_savedChildren) {
  try { _userStore.loadChildCompanies(JSON.parse(_savedChildren)); } catch (e) { /* ignore */ }
}
// Global open indicator: replace default dropdown arrow with feather chevron
vSelect.props.components.default = () => ({
  OpenIndicator: {
    render() {
      return h(VueFeather, { type: 'chevron-down', size: '18' });
    }
  }
});
app.component("v-select", vSelect);
app.component(VueFeather.name, VueFeather);
app.use(router);

// Toast configuration - Modern & Elegant
const toastOptions = {
  position: "top-right",
  timeout: 4000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: "button",
  icon: true,
  rtl: false,
  transition: "Vue-Toastification__fade",
  maxToasts: 3,
  newestOnTop: true
};
app.use(Toast, toastOptions);

app.component('VueDatePicker', VueDatePicker);
// v-safe-html: v-html sanitizado (DOMPurify) para contenido de usuario, API o IA.
app.directive('safe-html', safeHtml);
app.mount('#app');

// PostHog (src/plugins/analytics.js): carga diferida y apagado sin key. Si se
// recarga con la sesión abierta, se vuelve a identificar a la persona.
initAnalytics();
try {
  const savedUser = JSON.parse(localStorage.getItem('gs-user') || 'null');
  if (savedUser && localStorage.getItem('gs-tkn')) {
    identifyUser(savedUser, JSON.parse(localStorage.getItem('gs-company') || 'null'));
  }
} catch { /* sesión guardada ilegible: se identifica en el próximo login */ }